import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useSearchParams } from "react-router-dom";
import CashoutSection from "@/components/CashoutSection";

export default function Wallet() {
  const { refresh, user } = useAuth();
  const [data, setData] = useState({ transactions: [], stats: {} });
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);

  const load = async () => {
    const r = await api.get("/wallet");
    setData(r.data);
    await refresh();
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const { data: st } = await api.get(`/payments/status/${sid}`);
        if (st.payment_status === "paid" && st.credited) {
          toast.success(`+${st.chips.toLocaleString()} chips credited`);
          setParams({}, { replace: true });
          setPolling(false);
          load();
          return;
        }
        if (["failed", "expired"].includes(st.payment_status)) {
          toast.error("Payment " + st.payment_status);
          setParams({}, { replace: true });
          setPolling(false);
          return;
        }
      } catch { /* ignore */ }
      if (tries < 15) setTimeout(poll, 2000);
      else setPolling(false);
    };
    poll();
    if (params.get("cancelled")) {
      toast.info("Payment cancelled");
      setParams({}, { replace: true });
    }
  }, [params, setParams]); // eslint-disable-line

  return (
    <div className="space-y-8">
      <div>
        <div className="pill">Wallet</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2">Your ledger</h1>
      </div>

      {polling && <div className="vexis-card p-4 text-sm text-slate-300" data-testid="poll-status">Confirming payment with Stripe…</div>}

      <div className="grid md:grid-cols-3 gap-3">
        <div className="vexis-card p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Balance</div>
          <div className="mt-1 font-display text-3xl text-[color:var(--gold)]" data-testid="wallet-balance">{user?.chips?.toLocaleString?.() ?? 0}</div>
        </div>
        <div className="vexis-card p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Total wagered</div>
          <div className="mt-1 font-display text-3xl">{(data.stats?.total_wagered || 0).toLocaleString()}</div>
        </div>
        <div className="vexis-card p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Total won</div>
          <div className="mt-1 font-display text-3xl text-emerald-400">{(data.stats?.total_won || 0).toLocaleString()}</div>
        </div>
      </div>

      <div className="vexis-card overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="pill">Transactions</div>
          <button onClick={load} className="text-xs text-slate-400 hover:text-white" data-testid="wallet-refresh">Refresh</button>
        </div>
        <ul className="divide-y divide-white/5">
          {data.transactions.map((t) => (
            <li key={t._id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`tx-${t._id}`}>
              <div>
                <div className="capitalize text-slate-200">{t.type}</div>
                <div className="text-xs text-slate-500 font-mono">{new Date(t.created_at).toLocaleString()}{t.meta?.game ? ` · ${t.meta.game}` : ""}</div>
              </div>
              <div className={`font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.amount >= 0 ? "+" : ""}{t.amount.toLocaleString()}</div>
            </li>
          ))}
          {data.transactions.length === 0 && <li className="px-5 py-6 text-slate-500 text-sm">No transactions yet.</li>}
        </ul>
      </div>

      <CashoutSection />
    </div>
  );
}
